import { useState } from 'react'
import { Globe, Chrome, Zap, Lock, Wifi, BarChart3, Shield, ArrowRight } from 'lucide-react'

export default function WebBrowserPage() {
  const [stats] = useState({
    browsersActive: 8,
    usersConnected: 542,
    sessionsToday: 1247,
    avgSpeed: '2.3s',
  })

  const browsers = [
    {
      name: 'Google Chrome',
      emoji: '🔵',
      status: 'Integrated',
      color: 'from-blue-600 to-blue-500',
      description: 'Fast, reliable browser with seamless integration',
      features: ['Fast performance', 'Sync across devices', 'Extensions support', 'DevTools'],
      shortcut: 'Windows: Win+Shift+C',
    },
    {
      name: 'Microsoft Edge',
      emoji: '🔷',
      status: 'Integrated',
      color: 'from-cyan-600 to-cyan-500',
      description: 'Chromium-based with Microsoft integration',
      features: ['Performance', 'Collections', 'Sleep tabs', 'Vertical tabs'],
      shortcut: 'Windows: Win+Shift+E',
    },
    {
      name: 'Mozilla Firefox',
      emoji: '🦊',
      status: 'Integrated',
      color: 'from-orange-600 to-orange-500',
      description: 'Privacy-focused open-source browser',
      features: ['Privacy first', 'Open source', 'Container tabs', 'Extensions'],
      shortcut: 'Windows: Win+Shift+F',
    },
    {
      name: 'Brave Browser',
      emoji: '🦁',
      status: 'Integrated',
      color: 'from-red-600 to-red-500',
      description: 'Privacy-enhanced with built-in rewards',
      features: ['Ad blocking', 'Tracker blocking', 'BAT rewards', 'IPFS support'],
      shortcut: 'Windows: Win+Shift+B',
    },
    {
      name: 'Opera Browser',
      emoji: '🎭',
      status: 'Integrated',
      color: 'from-rose-600 to-rose-500',
      description: 'Feature-rich with built-in VPN',
      features: ['Built-in VPN', 'Crypto wallet', 'AI features', 'Fast speed'],
      shortcut: 'Windows: Win+Shift+O',
    },
    {
      name: 'Guardbot Web Browser',
      emoji: '🛡️',
      status: 'Premium',
      color: 'from-purple-600 to-purple-500',
      description: 'Advanced security browser built for Cybertection',
      features: ['AI Security', 'Threat detection', 'Privacy shield', 'Enterprise grade'],
      shortcut: 'Windows: Win+Shift+G',
    },
    {
      name: 'Tor Browser',
      emoji: '🧅',
      status: 'Integrated',
      color: 'from-indigo-600 to-indigo-500',
      description: 'Anonymous browsing with Tor network',
      features: ['Anonymity', 'No tracking', 'Relay routing', 'Privacy mode'],
      shortcut: 'Windows: Win+Shift+T',
    },
    {
      name: 'Vivaldi Browser',
      emoji: '💜',
      status: 'Integrated',
      color: 'from-violet-600 to-violet-500',
      description: 'Highly customizable with powerful features',
      features: ['Customization', 'Tab stacks', 'Split view', 'Notes integration'],
      shortcut: 'Windows: Win+Shift+V',
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="space-y-6">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 p-8 text-white shadow-2xl border border-blue-500/20">
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Globe className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Web Browser</h1>
                </div>
                <p className="text-blue-100 text-lg font-semibold">Multi-Browser Integration & Management</p>
                <p className="text-blue-200 text-sm mt-1">Chrome • Edge • Firefox • Brave • Opera • Guardbot • Tor • Vivaldi</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{stats.browsersActive}</div>
                <div className="text-sm text-blue-100">Browsers Available</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Active Browsers', value: stats.browsersActive, icon: Globe, color: 'from-blue-600 to-blue-500' },
            { label: 'Users Connected', value: stats.usersConnected, icon: Wifi, color: 'from-green-600 to-green-500' },
            { label: 'Sessions Today', value: stats.sessionsToday, icon: Zap, color: 'from-purple-600 to-purple-500' },
            { label: 'Avg Load Time', value: stats.avgSpeed, icon: BarChart3, color: 'from-cyan-600 to-cyan-500' },
          ].map((stat, idx) => (
            <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
              <p className="text-white text-3xl font-black mt-3">{stat.value}</p>
            </div>
          ))}
        </div>

        <div>
          <h2 className="text-3xl font-bold text-white mb-6">Integrated Web Browsers</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {browsers.map((browser, idx) => (
              <div key={idx} className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20">
                <div className={`h-1 w-full bg-gradient-to-r ${browser.color} rounded-full mb-4 shadow-lg`}></div>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-3">
                    <div className={`bg-gradient-to-br ${browser.color} p-3 rounded-lg text-2xl`}>
                      {browser.emoji}
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-xl">{browser.name}</h3>
                      <span className={`text-xs font-bold px-3 py-1 rounded-full inline-block mt-1 ${
                        browser.status === 'Premium'
                          ? 'bg-yellow-500/20 text-yellow-300'
                          : 'bg-green-500/20 text-green-300'
                      }`}>
                        {browser.status}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-blue-300 text-sm mb-4">{browser.description}</p>
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {browser.features.map((feature, fidx) => (
                      <span key={fidx} className="text-xs bg-slate-700/50 text-blue-300 px-2 py-1 rounded border border-blue-500/20">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-blue-500/20">
                  <span className="text-cyan-400 text-xs font-semibold">{browser.shortcut}</span>
                  <button className={`bg-gradient-to-r ${browser.color} hover:shadow-lg transition-all px-4 py-2 rounded-lg text-white text-sm font-bold`}>
                    Launch
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
